// src/screens/Final/FinalSummaryScreen.js
import React, { useContext } from 'react';
import CountContext from '../../context/CountContext.js';

export default function FinalSummaryScreen({ onNext, onBack }) {
  const { state } = useContext(CountContext);
  const {
    preCount,
    rolls,
    writtenTipConfirmed,
    tipBucket: { coins: tipBucketCoins, bills: tipBucketBills },
    drawerCoins,
    bills,
    tax,
    thresholds,
    report,
    vendorPayouts,
    overage,
    // pull these out of `report`
    report: {
      giftCardPayment,   // redeemed
      giftCardSold,      // un-redeemed
      totalSales,
      totalTax
    }
  } = state;

  // Rolls value
  const rollsValue =
    rolls.quarters * 10 +
    rolls.dimes * 5 +
    rolls.nickels * 2 +
    rolls.pennies * 0.5;

  // Total change (floor of coins)
  const changeTotal = Math.floor(rollsValue + tipBucketCoins + drawerCoins);

  // Bills value
  const billsValue =
    bills.ones * 1 +
    bills.fives * 5 +
    bills.tens * 10 +
    bills.twenties * 20 +
    bills.fifties * 50 +
    bills.hundreds * 100;

  // Net bills income (excludes starter)
  const netBillsIncome = billsValue - thresholds.starterCash;

  // split off the whole‑dollar coins
  const coinWhole = Math.floor(tipBucketCoins);

  // 5) Tips to set aside = floor(creditTips) + tipBucketBills
  // after:
  const creditTipFloor = Math.floor(report.creditTips);


  const adjustedSales = state.report.totalSales;

  const adjustedTax = state.report.totalTax;

  // Cash deposit after overage
  const depositCash = changeTotal + netBillsIncome - (overage || 0);

  const vendorTotal = vendorPayouts.reduce((sum, v) => sum + (v.amount || 0), 0);


  // Tips cash to set aside
  const tipCash = creditTipFloor + tipBucketBills + coinWhole;

  const totalDeposit = depositCash + tipCash;

  // Credit-card deposit
  const creditDeposit = report.totalCharges;

  // Grand sales deposit
  const grandSales = depositCash + creditDeposit;

  // Net sales after payouts and taxes
  const netSales = adjustedSales - adjustedTax;

  return (
    <div className="p-4 max-w-lg mx-auto bg-white rounded-2xl shadow-lg space-y-6">
      <h2 className="text-2xl font-bold text-center">Final Summary</h2>

      {/* Deposit Cards */}
      <div className="grid grid-cols-1 gap-4">
        <div className="p-4 bg-indigo-50 rounded-2xl border border-indigo-200">
          <div className="text-gray-700">Cash Deposit</div>
          <div className="text-3xl font-semibold">${depositCash.toFixed(2)}</div>
        </div>
        <div className="p-4 bg-green-50 rounded-2xl border border-green-200">
          <div className="text-gray-700">Tips to Set Aside</div>
          <div className="text-3xl font-semibold">${tipCash.toFixed(2)}</div>
        </div>
        <div className="p-4 bg-yellow-50 rounded-2xl border border-yellow-200">
          <div className="text-gray-700">Credit-Card Deposit</div>
          <div className="text-3xl font-semibold">${creditDeposit.toFixed(2)}</div>
        </div>
        <div className="text-sm text-gray-700">
          Combine Tip Cash and Business Cash into one bank deposit.
        </div>
        <div style={{ textAlign: 'center',padding: '16px', border: '2px solid #1e3a8a', borderRadius: '12px', background: '#eef2ff', marginTop: '12px' }} >
          <div className="text-gray-700 font-bold text-lg">
            Total Bank Deposit
          </div>

          <div style={{ fontSize: '36px', fontWeight: '900', color: '#1e3a8a', marginTop: '6px' }} >
            ${totalDeposit.toFixed(2)}
          </div>
        </div>
      </div>

      <h3 className="text-xl font-semibold">Pre‑Count</h3>
      <div>Store / Location: {state.preCount.store}</div>
      <div>Date: {state.preCount.date} | Shift: {state.preCount.shift}</div>
      <div>Starting Balance: ${state.preCount.startingBalance.toFixed(2)}</div>
      <div>Manager: {state.preCount.manager}</div>
      <hr className="my-4" />

      {/* Detailed Breakdown */}
      <div className="space-y-4">
        <h3 className="text-xl font-semibold">Rolls & Written Tips</h3>
        <div>Rolls Value: ${rollsValue.toFixed(2)}</div>
        <div>Written Tips Confirmed: {writtenTipConfirmed ? 'Yes' : 'No'}</div>

        {/* ← New Gift Cards section */}
        <h3 className="text-xl font-semibold">Gift Cards</h3>
        <div>Redeemed (Gift Card Payment): ${giftCardPayment.toFixed(2)}</div>
        <div>Un-redeemed Purchases: ${giftCardSold.toFixed(2)}</div>


        <h3 className="text-xl font-semibold">Tip & Drawer Coins</h3>
        <div>Tip-Bucket Coins: ${tipBucketCoins.toFixed(2)}</div>
        <div>Tip-Bucket Bills: ${tipBucketBills.toFixed(2)}</div>
        <div>Drawer Coins: ${drawerCoins.toFixed(2)}</div>
        <div>Total Change Counted: ${changeTotal.toFixed(2)}</div>

        <h3 className="text-xl font-semibold">Bill Counts</h3>
        <div>Ones: {bills.ones}, Fives: {bills.fives}, Tens: {bills.tens}</div>
        <div>Twenties: {bills.twenties}, Fifties: {bills.fifties}, Hundreds: {bills.hundreds}</div>
        <div>Total Bills Value: ${billsValue.toFixed(2)}</div>

        <h3 className="text-xl font-semibold">Reconciliation & Taxes</h3>
        <div>Tax Rate: {(tax.salesRate * 100).toFixed(2)}%</div>
        <div>Adjusted Sales (Final): ${totalSales.toFixed(2)}</div>
        <div>Adjusted Tax: ${totalTax.toFixed(2)}</div>
        <h3 className="text-xl font-semibold">Vendor Payouts</h3>
        {vendorPayouts.map((v, i) => (
          <div key={i}>{v.description}: ${v.amount.toFixed(2)}</div>
        ))}
        <div>Total Vendor Payouts: ${vendorTotal.toFixed(2)}</div>

        <h3 className="text-xl font-semibold">Net Sales </h3>
        <div>${netSales.toFixed(2)}</div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={async () => {
            try {
              // Grab the summary container’s HTML
              const summaryHtml = document.querySelector('.p-4.max-w-lg').outerHTML;
              // POST it to your API
              const res = await fetch('/api/send-summary', {

                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ summaryHtml }),
              });
              if (!res.ok) throw new Error('Failed to send email');
              alert('✅ Summary email sent!');
              onNext();
            } catch (err) {
              alert(`❌ ${err.message}`);
            }
          }}
          className="px-4 py-2 bg-blue-500 text-white rounded"
        >
          Done
        </button>

      </div>
    </div>
  );
}
